import { useEffect } from 'react'

import { enviarUso } from '@/lib/api'
import { useModoDeTeste } from '@/lib/camadaEmTeste'
import { useModoNoObservatorio } from '@/lib/tooltipEmTeste'

/*
 * O registro anônimo do uso do mapa público. O servidor só soma contadores por
 * dia; daqui sai só o tipo do que aconteceu, e nunca o que identifica a pessoa
 * ou o imóvel: sem cookie, sem identificador guardado, sem o número do recibo
 * buscado. O estado mora no módulo, e não no React, porque "uma vez por
 * carregamento da página" é a vida do módulo: recarregar começa do zero, e o
 * mapa que desmonta e monta de novo na mesma página não abre de novo.
 *
 * Só registra com o mapa público na tela (`useRegistroDoMapa`, no
 * observatório). Fora dele — a Área do Gestor — e nos modos de teste da gestão,
 * em que quem olha o mapa é a equipe, as ações não enviam nada.
 */

/** Município e bioma vão com o lugar: o código do IBGE ou o do bioma. */
type BuscaComLugar =
  | { tipo: 'busca'; busca: 'municipio'; lugar: { tipo: 'municipio'; codigo: string } }
  | { tipo: 'busca'; busca: 'bioma'; lugar: { tipo: 'bioma'; codigo: string } }

/** Estado não é lugar; o recibo nunca vai com o número. */
type BuscaSemLugar = { tipo: 'busca'; busca: 'estado' | 'recibo' }

/** O que uma ação da pessoa no mapa registra. */
export type EventoDeUso =
  | { tipo: 'leitura'; leitura: 'imovel' }
  | { tipo: 'camada'; camada: string }
  | BuscaComLugar
  | BuscaSemLugar
  | { tipo: 'exportacao' }

/** O maior bloco de navegação que a API aceita: quatro horas. */
export const TETO_DA_NAVEGACAO_S = 14_400

let mapaNaTela = false
let aberturaEnviada = false
let leituraEnviada = false

/**
 * Registra uma ação da pessoa no mapa. Sem o mapa público na tela, não faz
 * nada; a leitura da ficha vai só na primeira vez de cada carregamento.
 */
export function registrarUso(evento: EventoDeUso): void {
  if (!mapaNaTela) return
  if (evento.tipo === 'leitura') {
    if (leituraEnviada) return
    leituraEnviada = true
  }
  enviarUso(evento)
}

/** Os eventos de busca de um município e de um bioma, já com o lugar. */
export function buscaPorMunicipio(codigo: number): BuscaComLugar {
  return { tipo: 'busca', busca: 'municipio', lugar: { tipo: 'municipio', codigo: String(codigo) } }
}

export function buscaPorBioma(codigo: number): BuscaComLugar {
  return { tipo: 'busca', busca: 'bioma', lugar: { tipo: 'bioma', codigo: String(codigo) } }
}

/**
 * Conta o tempo em que a página fica visível e o envia em blocos: ao ficar
 * oculta (troca de aba, tela bloqueada) e ao ser descarregada. Cada bloco é o
 * que se acumulou desde o último envio, em segundos inteiros; menos de 1 s
 * espera o próximo, e acima do teto vai o teto. O relógio é o monotônico
 * (`performance.now`): acertar o relógio do sistema não cria nem some tempo.
 * Devolve quem para a contagem, enviando o que estiver acumulado.
 */
function contarNavegacao(): () => void {
  let visivelDesde: number | null =
    document.visibilityState === 'visible' ? performance.now() : null
  let acumuladoMs = 0

  function pausar() {
    if (visivelDesde === null) return
    acumuladoMs += performance.now() - visivelDesde
    visivelDesde = null
  }

  function enviarBloco() {
    pausar()
    const segundos = Math.floor(acumuladoMs / 1000)
    if (segundos < 1) return
    // O que passa do teto se perde: a API recusaria o bloco inteiro.
    acumuladoMs = segundos > TETO_DA_NAVEGACAO_S ? 0 : acumuladoMs - segundos * 1000
    enviarUso({ tipo: 'navegacao', segundos: Math.min(segundos, TETO_DA_NAVEGACAO_S) })
  }

  function aoMudarVisibilidade() {
    if (document.visibilityState === 'visible') {
      visivelDesde ??= performance.now()
    } else {
      enviarBloco()
    }
  }

  // A página que volta do cache de navegação (voltar do navegador) volta a contar.
  function aoMostrar() {
    if (document.visibilityState === 'visible') visivelDesde ??= performance.now()
  }

  document.addEventListener('visibilitychange', aoMudarVisibilidade)
  window.addEventListener('pagehide', enviarBloco)
  window.addEventListener('pageshow', aoMostrar)
  return () => {
    document.removeEventListener('visibilitychange', aoMudarVisibilidade)
    window.removeEventListener('pagehide', enviarBloco)
    window.removeEventListener('pageshow', aoMostrar)
    enviarBloco()
  }
}

/**
 * Liga o registro com o mapa público na tela: envia a abertura (uma por
 * carregamento) e conta o tempo de navegação. Devolve quem desliga, enviando o
 * tempo acumulado.
 */
export function iniciarRegistroDoMapa(): () => void {
  mapaNaTela = true
  if (!aberturaEnviada) {
    aberturaEnviada = true
    enviarUso({ tipo: 'abertura' })
  }
  const pararNavegacao = contarNavegacao()
  return () => {
    mapaNaTela = false
    pararNavegacao()
  }
}

/**
 * O registro do mapa público, montado no observatório. Nos modos de teste da
 * gestão (camada em teste, prévia do arquivo, posicionar ou testar um tooltip)
 * fica desligado, lido pelos mesmos leitores que montam esses modos.
 */
export function useRegistroDoMapa(): void {
  const emTesteDaCamada = useModoDeTeste() !== undefined
  const emTesteDoTooltip = useModoNoObservatorio() !== undefined
  const emTeste = emTesteDaCamada || emTesteDoTooltip
  useEffect(() => (emTeste ? undefined : iniciarRegistroDoMapa()), [emTeste])
}
